import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const SITE = 'https://puskica-kviz.rs';
const DEFAULT_TITLE = 'Puskica Kviz – testovi, lekcije i kvizovi';
const DEFAULT_DESCRIPTION = 'Puskica Kviz - platforma za učenje kroz testove, materijali i kvizovi iz različitih oblasti.';

// Putanja za koju je neka stranica sama postavila naslov/opis, da ih globalni reset ne pregazi.
let customMetaPath: string | null = null;

const setMeta = (selector: string, create: () => HTMLElement, attr: string, value: string) => {
    let el = document.head.querySelector<HTMLElement>(selector);
    if (!el) {
        el = create();
        document.head.appendChild(el);
    }
    el.setAttribute(attr, value);
};

const setDescription = (value: string) =>
    setMeta('meta[name="description"]', () => Object.assign(document.createElement('meta'), { name: 'description' }), 'content', value);

const setCanonical = (path: string) =>
    setMeta('link[rel="canonical"]', () => Object.assign(document.createElement('link'), { rel: 'canonical' }), 'href', SITE + path);

const setOgTag = (property: string, value: string) =>
    setMeta(`meta[property="${property}"]`, () => {
        const el = document.createElement('meta');
        el.setAttribute('property', property);
        return el;
    }, 'content', value);

const plainText = (text?: string) =>
    (text || '')
        .replace(/<[^>]*>/g, ' ')
        .replace(/[#*_`>$\\]/g, '')
        .replace(/\s+/g, ' ')
        .trim();

const shorten = (text: string, max = 155) => (text.length <= max ? text : text.slice(0, max - 1).trimEnd() + '…');

// Postavlja canonical na tačnu adresu trenutne stranice (index.html ima samo canonical za početnu),
// a naslov i opis vraća na podrazumevane dok stranica ne postavi sopstvene.
export const useCanonicalSync = () => {
    const { pathname } = useLocation();
    useEffect(() => {
        const path = pathname === '/' ? '/home' : pathname;
        setCanonical(path);
        setOgTag('og:url', SITE + path);
        if (customMetaPath !== pathname) {
            document.title = DEFAULT_TITLE;
            setDescription(DEFAULT_DESCRIPTION);
            setOgTag('og:title', DEFAULT_TITLE);
            setOgTag('og:description', DEFAULT_DESCRIPTION);
        }
    }, [pathname]);
};

// Naslov i opis stranice (kategorija, oblast, lekcija...) kad se podaci učitaju.
export const usePageMeta = (naziv?: string, opis?: string, kontekst?: string) => {
    const { pathname } = useLocation();
    useEffect(() => {
        if (!naziv) return;
        const title = `${naziv}${kontekst ? ' – ' + kontekst : ''} | Puskica Kviz`;
        const description = shorten(plainText(opis)) || `${naziv} – lekcije, pitanja i kvizovi na Puskica Kviz.`;
        customMetaPath = pathname;
        document.title = title;
        setDescription(description);
        setOgTag('og:title', title);
        setOgTag('og:description', description);
    }, [naziv, opis, kontekst, pathname]);
};
